// src/client/renderer/vfx/items/frost-blaster.ts
//
// Plan 17, Task 2.1 — Frost Blaster skillshot pool.
//
// One-shot continuous-beam (NOT jagged) cyan/white line from caster → target,
// with shatter sprites at the impact point.
// Max-active = 4. Animation: 200ms beam appearance + 400ms shatter.
// Total lifetime: 600ms (shatter drives the finish marker).
// Colour: 0x99ccff (icy cyan).
//
// GSAP: use tl.call(fn, undefined, absolutePos) — NOT eventCallback('onComplete').
// See gsap-registry.ts VIRTUAL_DURATION note for why.
//
// Resolvers:
//   item_cast   → phase: 'cast'   (manager fires audio cue before scene.add)
//   item_hit    → phase: 'impact' (no extra audio)
//   item_missed → phase: 'impact' (no targetPosition → beam dissipates mid-air)
//
// Audio:
//   Manager fires the item cue only for phase='cast'. Impact and miss visuals
//   are intentionally silent follow-up emitters.
//
// Registry tick contract (HF2):
//   pool.update() does NOT call registry.update(). The caller (item-vfx-manager
//   at Task 6.1, or tests directly) advances the shared registry once per frame.
//   All ~14 item pools share one registry; each pool advancing it independently
//   would multiply the tick rate by the pool count.

import * as THREE from 'three';
import type { ItemVfxPool, ItemVfxPoolDeps, ItemVfxSpawnArgs } from './item-vfx-types';
import type {
  GameEvent,
  ItemCastEvent,
  ItemHitEvent,
  ItemMissedEvent,
} from '../../../../shared/game/types';
import { disposeSkillshotGroup } from './skillshot-shared';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const MAX_ACTIVE = 4;
const BEAM_COLOUR = 0x99ccff; // icy cyan
const BEAM_APPEAR_DURATION = 0.2; // 200ms
const SHATTER_DURATION = 0.4; // 400ms shatter sprites
const TOTAL_LIFETIME = BEAM_APPEAR_DURATION + SHATTER_DURATION; // 600ms
const SHATTER_SCALE_END = 1.2;

// ─────────────────────────────────────────────────────────────────────────────
// Internal emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<ItemVfxPoolDeps['registry']['create']>;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory — createFrostBlasterPool
// ─────────────────────────────────────────────────────────────────────────────

export function createFrostBlasterPool(deps: ItemVfxPoolDeps): ItemVfxPool {
  const { scene, registry } = deps;
  const emitterStates = new Map<THREE.Object3D, EmitterState>();
  let disposed = false;

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(args: ItemVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;
    if (emitterStates.size >= MAX_ACTIVE) return null;

    const { casterPosition, targetPosition } = args;
    if (!casterPosition && !targetPosition) return null;

    const from = casterPosition ?? { x: 0, y: 0, z: 0 };
    const to = targetPosition ?? { x: from.x + 1, y: from.y, z: from.z };

    const group = new THREE.Group();

    // ── Thick continuous beam ────────────────────────────────────────────────
    const beamMaterial = new THREE.MeshBasicMaterial({
      color: BEAM_COLOUR,
      transparent: true,
      opacity: 0.0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const fromVec = new THREE.Vector3(from.x, from.y, from.z);
    const toVec = new THREE.Vector3(to.x, to.y, to.z);
    const beamVector = toVec.clone().sub(fromVec);
    const beamLength = Math.max(0.001, beamVector.length());
    const beamGeometry = new THREE.CylinderGeometry(0.055, 0.095, beamLength, 14, 1, true);
    const beamMesh = new THREE.Mesh(beamGeometry, beamMaterial);
    beamMesh.position.copy(fromVec).add(toVec).multiplyScalar(0.5);
    beamMesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), beamVector.normalize());
    beamMesh.scale.set(1, 0.04, 1);
    group.add(beamMesh);

    // ── Shatter sprite at impact point ───────────────────────────────────────
    const shatterMaterial = new THREE.SpriteMaterial({
      color: BEAM_COLOUR,
      transparent: true,
      opacity: 1.0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const shatter = new THREE.Sprite(shatterMaterial);
    shatter.position.set(to.x, to.y, to.z);
    shatter.scale.set(0, 0, 1);
    group.add(shatter);

    scene.add(group);

    // ── GSAP timeline ────────────────────────────────────────────────────────
    const handle = registry.create('frost_blaster');
    const tl = handle.timeline;

    const state: EmitterState = { handle, finished: false };

    // Beam opacity: 0 → 1 over BEAM_APPEAR_DURATION (starts immediately)
    const beamOpacityProxy = { value: 0.0 };
    tl.to(
      beamOpacityProxy,
      {
        value: 1.0,
        duration: BEAM_APPEAR_DURATION,
        ease: 'power2.in',
        onUpdate: () => {
          beamMaterial.opacity = beamOpacityProxy.value;
          beamMesh.scale.y = Math.max(0.04, beamOpacityProxy.value);
        },
      },
      0,
    );

    // Shatter: scale 0 → SHATTER_SCALE_END, starts at BEAM_APPEAR_DURATION
    tl.to(
      shatter.scale,
      {
        x: SHATTER_SCALE_END,
        y: SHATTER_SCALE_END,
        z: 1,
        duration: SHATTER_DURATION,
        ease: 'power2.out',
      },
      BEAM_APPEAR_DURATION,
    );

    // Shatter opacity: 1 → 0 over SHATTER_DURATION (starts at BEAM_APPEAR_DURATION)
    const shatterOpacityProxy = { value: 1.0 };
    tl.to(
      shatterOpacityProxy,
      {
        value: 0.0,
        duration: SHATTER_DURATION,
        ease: 'power2.in',
        onUpdate: () => {
          shatterMaterial.opacity = shatterOpacityProxy.value;
        },
      },
      BEAM_APPEAR_DURATION,
    );

    // Beam fade-out during shatter phase: 1 → 0
    const beamFadeProxy = { value: 1.0 };
    tl.to(
      beamFadeProxy,
      {
        value: 0.0,
        duration: SHATTER_DURATION,
        ease: 'power1.in',
        onUpdate: () => {
          beamMaterial.opacity = beamFadeProxy.value;
        },
      },
      BEAM_APPEAR_DURATION,
    );

    // Completion callback at TOTAL_LIFETIME absolute seconds.
    // MUST use tl.call(fn, undefined, absolutePos) — NOT eventCallback('onComplete').
    tl.call(
      () => {
        state.finished = true;
      },
      undefined,
      TOTAL_LIFETIME,
    );

    emitterStates.set(group, state);
    return group;
  }

  // ── update ─────────────────────────────────────────────────────────────────
  // Registry tick contract (HF2): pool.update does NOT advance the registry.
  // The shared registry is ticked by the caller (item-vfx-manager at Task 6.1)
  // exactly once per frame across all pools. Calling registry.update here would
  // advance it once per pool per frame (~14× too fast at wire-up time).

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    return new Set(emitterStates.keys());
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const state = emitterStates.get(emitter);
    if (!state) return true;
    return state.finished;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    const state = emitterStates.get(emitter);
    if (!state) return;
    state.handle.dispose();
    emitterStates.delete(emitter);
    scene.remove(emitter);
    disposeSkillshotGroup(emitter);
  }

  // ── dispose (F4) ───────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    for (const [emitter, state] of emitterStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    emitterStates.clear();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver — resolveFrostBlasterForEvent
// ─────────────────────────────────────────────────────────────────────────────

export function resolveFrostBlasterForEvent(
  event: GameEvent,
  getPositionForPlayer?: (playerId: string) => { x: number; y: number; z: number } | undefined,
): { poolId: 'frost_blaster'; args: ItemVfxSpawnArgs } | null {
  if (event.type !== 'item_cast' && event.type !== 'item_hit' && event.type !== 'item_missed') {
    return null;
  }

  if (event.itemId !== 'frost_blaster') return null;

  // After the type guard above, TypeScript narrows event to the discriminated
  // union of ItemCastEvent | ItemHitEvent | ItemMissedEvent. All three have
  // casterId: string (required, non-optional). targetId is required only on
  // ItemHitEvent; optional on ItemCastEvent and absent on ItemMissedEvent.
  // Access fields directly without unsafe `as` casts (SW6).

  let casterId: string;
  let targetId: string | undefined;

  if (event.type === 'item_cast') {
    const cast = event as ItemCastEvent;
    casterId = cast.casterId;
    targetId = cast.targetId;
  } else if (event.type === 'item_hit') {
    const hit = event as ItemHitEvent;
    casterId = hit.casterId;
    targetId = hit.targetId; // required on ItemHitEvent
  } else {
    // item_missed
    const missed = event as ItemMissedEvent;
    casterId = missed.casterId;
    targetId = missed.targetId; // optional on ItemMissedEvent
  }

  const casterPosition =
    casterId && getPositionForPlayer ? getPositionForPlayer(casterId) : undefined;
  const targetPosition =
    targetId && getPositionForPlayer ? getPositionForPlayer(targetId) : undefined;

  const phase: ItemVfxSpawnArgs['phase'] = event.type === 'item_cast' ? 'cast' : 'impact';

  return {
    poolId: 'frost_blaster',
    args: {
      phase,
      casterPosition,
      targetPosition,
    },
  };
}
